import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { saveGoalActionLog, fetchGoalActionLogs, selectActionLogs, selectActionLogsLoading } from '../../../redux/goalSlice';
import { selectUser } from '../../../redux/authSlice';
import CommonDrawerLayout from '../../../common_components/CommonDrawerLayout';
import { TextArea, FormField } from '../../../common_components/FormInputs';
import AppButton from '../../../common_components/AppButton';
import { toast } from '../../../common_components/Toast/ToastUtils';
const GoalActionDrawer = ({ open, onClose, onSaved, client, year_month, action_type, service_type, month_label, campaign_id, connection_id, isReadOnly }) => {
    const dispatch = useDispatch();
    const user = useSelector(selectUser);
    const logs = useSelector(selectActionLogs);
    const loading = useSelector(selectActionLogsLoading);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [visibleCount, setVisibleCount] = useState(4);
    useEffect(() => {
        if (open && client) {
            dispatch(fetchGoalActionLogs({
                campaign_id,
                activity_type: `${action_type}_${service_type}`,
                month_label
            }));
            setComment('');
            setVisibleCount(4);
        }
    }, [open, client, year_month, action_type, service_type, month_label, dispatch]);
    const handleSave = async () => {
        if (!comment.trim()) {
            toast.error('Please enter a comment');
            return;
        }
        if (!client || !user)
            return;
        setSubmitting(true);
        try {
            await dispatch(saveGoalActionLog({
                client_id: client.id,
                campaign_id,
                connection_id,
                activity_type: `${action_type}_${service_type}`,
                month_label,
                comment,
                user_id: String(user.id || user._id)
            })).unwrap();
            setComment('');
            // Refetch logs to get populated user names and roles from the aggregation
            dispatch(fetchGoalActionLogs({
                campaign_id,
                activity_type: `${action_type}_${service_type}`,
                month_label
            }));
            onSaved?.();
        }
        catch (error) {
            console.error('Failed to save log', error);
        }
        finally {
            setTimeout(() => setSubmitting(false), 500);
        }
    };
    const formatTime = (dateStr) => {
        if (!dateStr)
            return "";
        const d = new Date(dateStr);
        const diffInSec = Math.floor((new Date().getTime() - d.getTime()) / 1000);
        if (diffInSec < 60)
            return "Just now";
        const diffInMin = Math.floor(diffInSec / 60);
        if (diffInMin < 60)
            return `${diffInMin} min ago`;
        const diffInHours = Math.floor(diffInMin / 60);
        if (diffInHours < 24)
            return `${diffInHours} hours ago`;
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });
    };
    const getTitle = () => {
        const typeStr = action_type === 'plan' ? 'Plan For Hitting Goals' : 'Monthly Progress Notes';
        return `${typeStr} : ${client?.name || ''}`;
    };
    // Helper for heading in history
    const historyHeading = action_type === 'plan' ? 'Plan for Hitting Goals' : 'Add Progress Notes';
    const textareaLabel = action_type === 'plan' ? `Write for ${month_label} Plan for Hitting Goals` : `Write for Monthly Progress Notes`;
    const buttonLabel = action_type === 'plan' ? 'Add Comment' : 'Add Notes';
    const buttonIcon = action_type === 'plan' ? '/assets/Icon.svg' : '/assets/note.svg'; // Assuming these exist or fallback
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: getTitle(), width: "md", children: _jsxs(Box, { sx: { p: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }, children: [_jsxs(Paper, { variant: "outlined", sx: { p: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }, children: [_jsx(Typography, { sx: { color: '#FF008E', fontWeight: 600, mb: '12px', fontSize: '14px', borderBottom: '1px solid #EAECF0', pb: '8px' }, children: historyHeading }), _jsx(FormField, { children: _jsx(TextArea, { label: textareaLabel, required: true, minRows: 4, maxRows: 6, placeholder: isReadOnly ? "Editing is disabled for inactive clients in past months" : "Write here...", value: comment, onChange: (e) => setComment(e.target.value), disabled: isReadOnly, style: {
                                    opacity: isReadOnly ? 0.6 : 1,
                                    cursor: isReadOnly ? 'not-allowed' : 'text'
                                } }) }), _jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: '12px' }, children: _jsx(AppButton, { variant: "contained", onClick: handleSave, loading: submitting, disabled: isReadOnly, sx: {
                                    background: isReadOnly ? '#9CA3AF' : '#FF008E',
                                    '&:hover': { background: isReadOnly ? '#9CA3AF' : '#db2777' },
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    px: '20px',
                                    opacity: isReadOnly ? 0.6 : 1,
                                    cursor: isReadOnly ? 'not-allowed' : 'pointer'
                                }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("img", { src: buttonIcon, alt: "", width: 16 }), buttonLabel] }) }) })] }), _jsxs(Paper, { variant: "outlined", sx: { p: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }, children: [_jsx(Typography, { sx: { color: '#FF008E', fontWeight: 600, mb: '24px', fontSize: '14px', borderBottom: '1px solid #EAECF0', pb: '8px' }, children: "History" }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 }, children: _jsx(CircularProgress, { size: 24, sx: { color: '#FF008E' } }) })) : logs.length === 0 ? (_jsx(Typography, { sx: { color: '#6B7280', fontSize: '14px', textAlign: 'center', py: 4 }, children: "No action history yet for this goal." })) : (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '24px' }, children: [logs.slice(0, visibleCount).map((log, index) => (_jsxs(Box, { sx: { display: 'flex', gap: '12px', position: 'relative' }, children: [index < Math.min(visibleCount, logs.length) - 1 && (_jsx(Box, { sx: {
                                                position: 'absolute',
                                                left: '18px',
                                                top: '36px',
                                                bottom: '-28px',
                                                width: '1px',
                                                background: '#E5E7EB',
                                            } })), _jsx(Box, { sx: {
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '8px',
                                                border: '1px solid #E5E7EB',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#fff',
                                                color: '#3B82F6',
                                                fontWeight: 600,
                                                fontSize: '14px',
                                                flexShrink: 0,
                                                zIndex: 1
                                            }, children: ((log.user_id?.first_name?.[0] || log.sender_first_name?.[0] || '') + (log.user_id?.last_name?.[0] || log.sender_last_name?.[0] || '')) || 'U' }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsxs(Typography, { sx: { fontSize: '14px', fontWeight: 600, color: '#111827' }, children: [log.user_id?.first_name || log.sender_first_name, " ", log.user_id?.last_name || log.sender_last_name, _jsxs("span", { style: { fontWeight: 400, color: '#6B7280', marginLeft: '4px' }, children: ["(", log.sender_designation || 'Account Manager', ")"] })] }), _jsx(Typography, { sx: { fontSize: '12px', color: '#6B7280', mb: '8px' }, children: formatTime(log.createdAt) }), _jsx(Typography, { sx: { fontSize: '14px', color: '#4B5563', lineHeight: 1.5 }, children: log.comment })] })] }, log._id))), logs.length > visibleCount && (_jsxs(Box, { sx: { display: 'flex', mt: '8px' }, children: [_jsx(Box, { sx: {
                                                width: '24px',
                                                height: '24px',
                                                borderLeft: '1px solid #E5E7EB',
                                                borderBottom: '1px solid #E5E7EB',
                                                borderBottomLeftRadius: '8px',
                                                ml: '18px',
                                                mt: '-24px',
                                                mr: '8px'
                                            } }), _jsx(AppButton, { variant: "outlined", size: "small", onClick: () => setVisibleCount(prev => prev + 5), sx: {
                                                textTransform: 'none',
                                                color: '#374151',
                                                borderColor: '#E5E7EB',
                                                fontSize: '12px',
                                                '&:hover': { background: '#F9FAFB', borderColor: '#D1D5DB' }
                                            }, children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '8px' }, children: [_jsx("img", { src: "/assets/refresh.svg", alt: "", width: 14 }), "View More (", logs.length - visibleCount, ")"] }) })] }))] }))] })] }) }));
};
export default GoalActionDrawer;
